import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronLeft, Check, AlertCircle } from 'lucide-react';
import AdminLayout from '@/components/layout/AdminLayout';
import EscalateJobModal from '@/components/modals/EscalateJobModal';
import CompletionApprovalModal from '@/components/modals/CompletionApprovalModal';
import ReleasePayoutModal from '@/components/modals/ReleasePayoutModal';
import HoldPayoutModal from '@/components/modals/HoldPayoutModal';
import apiService from '@/services/api';

interface QAJobDetail {
  job_id: number;
  qa_id: string;
  service_name: string;
  provider_name: string;
  customer_name: string;
  property_address: string;
  job_completed_at: string;
  total_amount_pence: number;
  total_pounds: string;
  platform_margin_pounds: string;
  provider_payout_pounds: string;
  qa_status: string | null;
  qa_notes: string | null;
  qa_result: string | null;
  customer_rating: number | null;
  customer_comment: string | null;
  reviewer_name: string | null;
  documents: Array<{ id: number; document_type: string; file_name: string; file_url: string }>;
}

const QAReviewPage = () => {
  const navigate = useNavigate();
  const { jobId: routeJobId } = useParams<{ jobId: string }>();
  const realJobId = routeJobId ? Number(routeJobId) : null;

  const [jobData, setJobData] = useState<QAJobDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [assessmentForm, setAssessmentForm] = useState({
    evidenceSatisfactory: false,
    photosPresent: false,
    scopeMet: false,
    complianceMet: false,
    noIssues: false
  });
  const [overallResult, setOverallResult] = useState('');
  const [assessmentNotes, setAssessmentNotes] = useState('');
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [showEscalateJobModal, setShowEscalateJobModal] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showReleasePayoutModal, setShowReleasePayoutModal] = useState(false);
  const [showHoldPayoutModal, setShowHoldPayoutModal] = useState(false);

  useEffect(() => {
    if (!realJobId) { setLoading(false); return; }
    const fetchJob = async () => {
      try {
        const res: any = await apiService.get(`/admin/qa-jobs/${realJobId}`);
        if (res?.success && res?.data) {
          setJobData(res.data);
          if (res.data.qa_notes) setAssessmentNotes(res.data.qa_notes);
          if (res.data.qa_result) setOverallResult(res.data.qa_result);
        } else {
          setLoadError(res?.message || 'Failed to load job details');
        }
      } catch (e: any) {
        setLoadError(e?.message || 'Network error loading job');
      } finally {
        setLoading(false);
      }
    };
    fetchJob();
  }, [realJobId]);

  const providerName = jobData?.provider_name || '—';
  const totalPounds = jobData?.total_pounds || '0.00';
  const platformMarginPounds = jobData?.platform_margin_pounds || '0.00';
  const providerPayoutPounds = jobData?.provider_payout_pounds || '0.00';
  const jobLabel = jobData?.qa_id || (realJobId ? `#JOB-${realJobId}` : '');
  const completedDate = jobData?.job_completed_at
    ? new Date(jobData.job_completed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

  const beforeDocs = jobData?.documents.filter(d => d.document_type === 'before_photo') || [];
  const afterDocs = jobData?.documents.filter(d => d.document_type === 'after_photo') || [];
  const otherDocs = jobData?.documents.filter(d => !['before_photo','after_photo'].includes(d.document_type)) || [];

  return (
    <>
      <Helmet>
        <title>{jobLabel ? `QA Review - ${jobLabel} - Virewire` : 'QA Review - Virewire'}</title>
      </Helmet>

      <AdminLayout>
        <div className="bg-gray-50 p-8 min-h-screen">
          {/* Breadcrumb & Header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <button onClick={() => navigate('/admin/qa')} className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900">
                <ChevronLeft className="w-4 h-4" />
                OPERATIONS
              </button>
              <span className="text-sm text-gray-600">/ QA REVIEW</span>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {jobLabel ? `${jobLabel} QA Review` : 'QA Review'}
            </h1>

            {loading && <p className="text-sm text-gray-500">Loading job details...</p>}
            {loadError && <p className="text-sm text-red-600">{loadError}</p>}

            {/* Meta Info */}
            {!loading && (
              <div className="flex items-center gap-8 text-sm">
                <div className="flex items-center gap-2 text-gray-700">
                  <span className="text-teal-600">📅</span>
                  <span>Completed {completedDate}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <span className="text-teal-600">👤</span>
                  <span>Provider: {providerName}</span>
                </div>
                <div className="flex items-center gap-2 font-bold text-gray-900">
                  <span className="text-teal-600">💰</span>
                  <span>Payout: £{totalPounds}</span>
                </div>
              </div>
            )}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-3 gap-8">
            {/* Left Content (2/3 width) */}
            <div className="col-span-2 space-y-8">
              {/* Completion Evidence */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-5 h-1 bg-teal-600 rounded"></div>
                  <h2 className="text-lg font-bold text-gray-900">Completion Evidence</h2>
                </div>

                {/* Before/After Images */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <p className="text-xs uppercase font-bold text-gray-600 mb-2">BEFORE INTERVENTION</p>
                    {beforeDocs.length > 0 ? (
                      <img src={beforeDocs[0].file_url} alt="Before" className="w-full h-32 object-cover rounded-lg" />
                    ) : (
                      <div className="w-full h-32 bg-gray-100 rounded-lg flex items-center justify-center text-xs text-gray-400">No before photo uploaded</div>
                    )}
                  </div>
                  <div>
                    <p className="text-xs uppercase font-bold text-gray-600 mb-2">AFTER COMPLETION</p>
                    {afterDocs.length > 0 ? (
                      <img src={afterDocs[0].file_url} alt="After" className="w-full h-32 object-cover rounded-lg" />
                    ) : (
                      <div className="w-full h-32 bg-gray-100 rounded-lg flex items-center justify-center text-xs text-gray-400">No after photo uploaded</div>
                    )}
                  </div>
                </div>

                {/* Documents */}
                {otherDocs.length > 0 && (
                  <div className="mb-6">
                    <p className="text-sm font-bold text-gray-900 mb-3">Submitted Documents</p>
                    <div className="space-y-2">
                      {otherDocs.map(doc => (
                        <div key={doc.id} className="flex items-center gap-3">
                          <div className="w-5 h-5 rounded bg-teal-600 flex items-center justify-center flex-shrink-0">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                          <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="text-sm text-teal-700 hover:underline">
                            {doc.file_name || doc.document_type}
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {loading && <p className="text-sm text-gray-400 text-center py-4">Loading documents...</p>}
                {!loading && jobData && beforeDocs.length === 0 && afterDocs.length === 0 && otherDocs.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-2">No documents uploaded for this job yet.</p>
                )}
              </div>

              {/* Scope Reconciliation */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-5 h-1 bg-teal-600 rounded"></div>
                  <h2 className="text-lg font-bold text-gray-900">Scope Reconciliation</h2>
                </div>
                {jobData?.service_name ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50">
                          <th className="text-left px-4 py-3 text-xs font-bold text-gray-600 uppercase">Service</th>
                          <th className="text-left px-4 py-3 text-xs font-bold text-gray-600 uppercase">Total Value</th>
                          <th className="text-left px-4 py-3 text-xs font-bold text-gray-600 uppercase">Provider</th>
                          <th className="text-left px-4 py-3 text-xs font-bold text-gray-600 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-gray-200">
                          <td className="px-4 py-4 text-sm font-medium text-gray-900">{jobData.service_name}</td>
                          <td className="px-4 py-4 text-sm text-gray-700">£{totalPounds}</td>
                          <td className="px-4 py-4 text-sm text-gray-700">{providerName}</td>
                          <td className="px-4 py-4">
                            <span className="px-3 py-1 rounded text-xs font-bold bg-green-100 text-green-700">COMPLETED</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">No scope data available.</p>
                )}
              </div>

              {/* Customer Feedback */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-5 h-1 bg-teal-600 rounded"></div>
                  <h2 className="text-lg font-bold text-gray-900">Customer Feedback</h2>
                </div>

                {jobData?.customer_rating ? (
                  <div className="flex gap-8">
                    <div className="flex flex-col items-center gap-3">
                      <p className="text-4xl font-bold text-gray-900">{jobData.customer_rating}.0</p>
                      <div className="flex gap-1">
                        {[...Array(5)].map((_, i) => (
                          <span key={i} className={`text-2xl ${i < jobData.customer_rating! ? 'text-yellow-400' : 'text-gray-300'}`}>★</span>
                        ))}
                      </div>
                    </div>
                    <div className="flex-1">
                      {jobData.customer_comment && (
                        <p className="text-sm text-gray-700 mb-4 leading-relaxed">"{jobData.customer_comment}"</p>
                      )}
                      {jobData.reviewer_name && (
                        <div className="inline-block mt-4">
                          <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded">
                            <span className="text-xs font-bold bg-gray-300 rounded px-2 py-1">
                              {jobData.reviewer_name.split(' ').map(n => n[0]).join('').toUpperCase()}
                            </span>
                            <span className="text-xs font-semibold text-gray-700">{jobData.reviewer_name.toUpperCase()}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">No customer feedback submitted for this job yet.</p>
                )}
              </div>
            </div>

            {/* Right Sidebar (1/3 width) */}
            <div className="space-y-6">
              {/* QA Assessment Form */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-sm font-bold text-gray-900 mb-4">QA Assessment Form</h3>
                <div className="space-y-4">
                  {[
                    { key: 'evidenceSatisfactory', label: 'Evidence satisfactory?' },
                    { key: 'photosPresent', label: 'Photos present?' },
                    { key: 'scopeMet', label: 'Scope met?' },
                    { key: 'complianceMet', label: 'Compliance met?' },
                    { key: 'noIssues', label: 'No issues?' }
                  ].map((checkbox) => (
                    <label key={checkbox.key} className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={assessmentForm[checkbox.key as keyof typeof assessmentForm]}
                        onChange={(e) => setAssessmentForm({
                          ...assessmentForm,
                          [checkbox.key]: e.target.checked
                        })}
                        className="w-4 h-4 rounded border border-gray-300"
                      />
                      <span className="text-sm text-gray-700">{checkbox.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Overall Result */}
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
                <h3 className="text-xs font-bold text-gray-600 uppercase mb-4">Overall Result</h3>
                <div className="space-y-3">
                  {[
                    { value: 'pass', label: 'Pass' },
                    { value: 'conditional', label: 'Conditional Pass' },
                    { value: 'fail', label: 'Fail', color: 'text-red-600' }
                  ].map((option) => (
                    <label key={option.value} className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="overallResult"
                        value={option.value}
                        checked={overallResult === option.value}
                        onChange={(e) => setOverallResult(e.target.value)}
                        className="w-4 h-4"
                      />
                      <span className={`text-sm font-medium ${option.color || 'text-gray-900'}`}>
                        {option.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Assessment Notes */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-xs font-bold text-gray-600 uppercase mb-4">Assessment Notes</h3>
                <textarea
                  value={assessmentNotes}
                  onChange={(e) => setAssessmentNotes(e.target.value)}
                  placeholder="Add details for the provider or internal team..."
                  className="w-full h-32 p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                />
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={() => setShowEscalateJobModal(true)}
                    variant="outline"
                    className="text-red-600 border-red-300 hover:bg-red-50 font-semibold h-10"
                  >
                    ! Escalate Issue
                  </Button>
                  <Button
                    onClick={() => setShowHoldPayoutModal(true)}
                    className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold h-10"
                  >
                    ⏸ Hold Payout
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={() => setShowCompletionModal(true)}
                    className="bg-green-600 hover:bg-green-700 text-white font-semibold h-10"
                  >
                    ✓ Approve & Close Job
                  </Button>
                  <Button
                    onClick={() => setShowReleasePayoutModal(true)}
                    className="bg-teal-600 hover:bg-teal-700 text-white font-semibold h-10"
                  >
                    Release Payout
                  </Button>
                </div>
                <Button
                  variant="outline"
                  onClick={async () => {
                    if (!realJobId) { alert('Job id missing in URL'); return; }
                    const reason = window.prompt('Reason for returning this job to the provider?');
                    if (!reason || !reason.trim()) return;
                    try {
                      const res: any = await apiService.request(`/admin/bookings/${realJobId}/return`, 'POST', { reason });
                      if (res?.success) {
                        alert(res.message || 'Returned to provider');
                        navigate('/admin/qa');
                      } else {
                        alert('Error: ' + (res?.message || 'Failed to return job'));
                      }
                    } catch (e: any) {
                      alert('Failed: ' + (e?.message || 'Network error'));
                    }
                  }}
                  className="w-full border-orange-500 text-orange-600 hover:bg-orange-50 font-semibold h-10"
                >
                  Return for More Evidence
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowEscalateJobModal(true)}
                  className="w-full border-red-500 text-red-600 hover:bg-red-50 font-semibold h-10"
                >
                  Escalate Issue
                </Button>
              </div>

              {/* Footer Note */}
              <p className="text-xs text-gray-600 text-center mt-6">
                By approving, you confirm that all works meet the safety standards defined in the SLA for Job {realJobId ? `#JOB-${realJobId}` : 'on this page'}.
              </p>
            </div>
          </div>
        </div>

        {/* Payout Confirmation Modal */}
        {showPayoutModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl">
              {/* Icon */}
              <div className="flex justify-center mb-6">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                  <Check className="w-6 h-6 text-teal-600" />
                </div>
              </div>

              {/* Title & Subtitle */}
              <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">Approve & Release Payout</h2>
              <p className="text-xs uppercase font-bold text-gray-500 text-center mb-6">Confirming closure for job {jobLabel}</p>

              {/* Content Box */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-700">
                  Total payout of <span className="font-bold">£{totalPounds}</span> will be released to <span className="font-bold">{providerName}</span>. The customer will be notified of job completion.
                </p>
              </div>

              {/* Financial Breakdown */}
              <div className="grid grid-cols-2 gap-6 mb-6 pb-6 border-b border-gray-200">
                <div>
                  <p className="text-xs uppercase font-bold text-gray-600 mb-2">Platform Margin (12%)</p>
                  <p className="text-xl font-bold text-teal-600">£{platformMarginPounds}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase font-bold text-gray-600 mb-2">Final Dispersal</p>
                  <p className="text-xl font-bold text-gray-900">£{providerPayoutPounds}</p>
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-700">
                  Funds will be available in the provider's wallet within 2-4 business hours.
                </p>
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={() => setShowPayoutModal(false)}
                  variant="outline"
                  className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    setShowPayoutModal(false);
                    // Handle payout confirmation
                  }}
                  className="flex-1 bg-teal-600 hover:bg-teal-700 text-white font-semibold"
                >
                  Confirm & Release Payout
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Escalate Job Modal */}
        <EscalateJobModal
          jobId={jobLabel}
          currentStatus="REVIEW_PENDING"
          isOpen={showEscalateJobModal}
          onClose={() => setShowEscalateJobModal(false)}
          onConfirm={async (escalationType, priorityLevel, adminAssignment, notes) => {
            if (!realJobId) {
              alert('Job id missing in URL');
              return;
            }
            const extra = [adminAssignment && `assigned to ${adminAssignment}`, notes].filter(Boolean).join(' | ');
            const reason = extra ? `${escalationType} — ${extra}` : escalationType;
            try {
              const res: any = await apiService.request(
                `/admin/bookings/${realJobId}/escalate`,
                'POST',
                { escalationReason: reason, priority: priorityLevel || 'normal' }
              );
              if (res?.success) {
                alert(res.message || 'Job escalated');
                setShowEscalateJobModal(false);
              } else {
                alert('Error: ' + (res?.message || 'Failed to escalate'));
              }
            } catch (e: any) {
              alert('Failed: ' + (e?.message || 'Network error'));
            }
          }}
        />

        {/* Completion Approval Modal */}
        <CompletionApprovalModal
          jobId={jobLabel}
          totalPayout={`£${totalPounds} GBP`}
          getQAProgress={() => ({
            completed: 4,
            total: 4,
          })}
          qaItems={[
            { name: 'Site Media', completed: true },
            { name: 'Client Sign-off', completed: true },
            { name: 'Compliance', completed: true },
            { name: 'Final Audit', completed: true },
          ]}
          isOpen={showCompletionModal}
          onClose={() => setShowCompletionModal(false)}
          onConfirm={async (releasePayload, internalNote) => {
            if (!realJobId) {
              alert('Job id missing in URL');
              return;
            }
            try {
              const res: any = await apiService.request(
                `/admin/bookings/${realJobId}/completion`,
                'POST',
                {
                  completionNotes: internalNote || 'QA-approved completion',
                  releasePayload: releasePayload || null,
                }
              );
              if (res?.success) {
                alert(res.message || 'Completion approved');
                setShowCompletionModal(false);
                navigate('/admin/qa');
              } else {
                alert('Error: ' + (res?.message || 'Failed to approve completion'));
              }
            } catch (e: any) {
              alert('Failed: ' + (e?.message || 'Network error'));
            }
          }}
        />

        {/* Release Payout Modal */}
        <ReleasePayoutModal
          providerName={providerName}
          providerRole="Service Provider"
          netPayout={`£${providerPayoutPounds}`}
          relatedJobIds={realJobId ? [`#JOB-${realJobId}`] : []}
          paymentMethod="Direct Transfer"
          bankAccount="On file"
          isOpen={showReleasePayoutModal}
          onClose={() => setShowReleasePayoutModal(false)}
          onConfirm={async (_confirmChecks) => {
            if (!realJobId) {
              alert('Job id missing in URL');
              return;
            }
            try {
              const res: any = await apiService.request(
                `/admin/qa-jobs/${realJobId}/release-payout`,
                'POST',
                {}
              );
              if (res?.success) {
                alert(res.message || 'Payout released');
                setShowReleasePayoutModal(false);
              } else {
                alert('Error: ' + (res?.message || 'Failed to release payout'));
              }
            } catch (e: any) {
              alert('Failed: ' + (e?.message || 'Network error'));
            }
          }}
        />

        {/* Hold Payout Modal */}
        <HoldPayoutModal
          payoutId={realJobId ? `PAY-${realJobId}` : 'PAY-—'}
          payoutAmount={`£${totalPounds}`}
          isOpen={showHoldPayoutModal}
          onClose={() => setShowHoldPayoutModal(false)}
          onConfirm={async (holdReason, internalNotes, notifyProvider) => {
            if (!realJobId) {
              alert('Job id missing in URL');
              return;
            }
            try {
              const res: any = await apiService.request(
                `/admin/qa-jobs/${realJobId}/hold-payout`,
                'POST',
                { holdReason, internalNotes, notifyProvider }
              );
              if (res?.success) {
                alert(res.message || 'Payout held');
                setShowHoldPayoutModal(false);
              } else {
                alert('Error: ' + (res?.message || 'Failed to hold payout'));
              }
            } catch (e: any) {
              alert('Failed: ' + (e?.message || 'Network error'));
            }
          }}
        />
      </AdminLayout>
    </>
  );
};

export default QAReviewPage;
